import { DateTime } from 'luxon';
import { IntlShape } from 'react-intl';

import {
  BPMonitor,
  bodyScale,
  icarehome2,
  glucometer,
  andMedicalScale,
  andMedicalBP,
  andMedicalLargeCuff,
  andBpmGen3Cuffs,
  garminVivoFit4,
  garminVivoSmart4,
  mirSpirobankSmart,
  mirSmartOne,
  noninOx,
  rocheGuide,
  rocheInstant,
  rocheInstantStrips,
  samsungA7Galaxy,
  galaxyA7BookCover,
  omronM7Intelli,
  samsungGalaxyTabA8,
  ipulseox,
  transtekBGM,
  transtekBPM,
  transtekScale,
  csDirectBPM,
  csDirectBPMXL,
  csDirectScale,
  iBP,
  andBpmGen3,
  iGlucoseTouch
} from 'assets';
import {
  DropOutlineDexcomMiddleIcon,
  FourgDropO2OutlineIcon,
  FourgDropOutlineIcon,
  FourgPressureMonitorOutlineIcon,
  FourgScaleOutlineIcon
} from 'assets/icons';
import { BODYTRACE_MANUFACTURER_NAME, smartMeterManufacturerNames } from 'constants/constants';

/**
 * Whether a catalogue item ships through Amazon multi-channel fulfillment.
 *
 * The API spells the provider `Amazon`; one call site compared it exactly and another lower-cased
 * it first, so this is the one place that decides.
 */
export const isAmazonFulfilled = (shippingProvider?: string | null): boolean =>
  (shippingProvider ?? '').toLowerCase() === 'amazon';

const DELIVERY_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric'
};

/**
 * An arrival window as the device list and the speed cards show it, date only, in the viewer's
 * locale: `Mon, Sep 28 – Wed, Sep 30`, or a single `Fri, Sep 25` when both ends fall on the same
 * calendar day. `null` when either end is missing or unparseable; the caller decides between
 * `--` and "Estimate unavailable".
 *
 * Amazon returns instants, and a promised day belongs to the destination's calendar, not the
 * viewer's: both ends are read in UTC so every viewer sees the same day. The en dash is
 * deliberate: the copy convention bans em dashes only.
 */
export const formatDeliveryWindow = (
  start: string | null | undefined,
  end: string | null | undefined,
  locale: string
): string | null => {
  if (!start || !end) return null;
  const startDate = DateTime.fromISO(start, { zone: 'utc' }).setLocale(locale);
  const endDate = DateTime.fromISO(end, { zone: 'utc' }).setLocale(locale);
  if (!startDate.isValid || !endDate.isValid) return null;
  const startText = startDate.toLocaleString(DELIVERY_DATE_FORMAT);
  if (startDate.hasSame(endDate, 'day')) return startText;
  return `${startText} – ${endDate.toLocaleString(DELIVERY_DATE_FORMAT)}`;
};

/** A catalogue row the cart can hold: a device, a standalone accessory, or a device's cuff option. */
export interface ICatalogEntry {
  deviceOrderingId: string;
  sku: string;
  displayName: string;
  shippingProvider: string;
}

/**
 * Finds a cart SKU in the catalogue, looking at the devices themselves and then at their options.
 *
 * A cuff ordered with a monitor is an option of that monitor, so it has no shipping provider of
 * its own: it travels in the monitor's shipment and inherits the monitor's provider.
 */
export const resolveCatalogEntry = (
  sku: string,
  catalog: IDeviceOrderingCatalogItem[]
): ICatalogEntry | undefined => {
  const device = catalog.find((d) => d.sku === sku);
  if (device) {
    return {
      deviceOrderingId: device.deviceOrderingId,
      sku: device.sku,
      displayName: device.displayName,
      shippingProvider: device.shippingProvider
    };
  }
  for (const parent of catalog) {
    const option = parent.options?.find((o) => o.sku === sku);
    if (option) {
      return {
        deviceOrderingId: option.deviceOrderingId,
        sku: option.sku,
        displayName: option.displayName,
        shippingProvider: parent.shippingProvider
      };
    }
  }
  return undefined;
};

/**
 * The cart (`sku -> quantity`) as order items, dropping empty lines and SKUs the catalogue does
 * not know. The order payload builder keeps its own stricter version that throws on an unknown
 * SKU; here an unknown SKU only means "no estimate for it".
 */
export const resolveCartItems = (
  cart: Record<string, number>,
  catalog: IDeviceOrderingCatalogItem[]
): (IBulkDeviceOrderItem & Pick<ICatalogEntry, 'shippingProvider'>)[] =>
  Object.entries(cart)
    .filter(([, quantity]) => Number(quantity) > 0)
    .flatMap(([sku, quantity]) => {
      const entry = resolveCatalogEntry(sku, catalog);
      return entry
        ? [
            {
              deviceOrderingId: entry.deviceOrderingId,
              quantity: Number(quantity),
              shippingProvider: entry.shippingProvider
            }
          ]
        : [];
    });

/**
 * Gets the images for 4g devices
 * @param {string} deviceName the name of the device
 * @returns The url of the device image
 */
export const getDeviceImage = (deviceName: string) => {
  switch (deviceName) {
    case 'icareHome2DisplayKey':
      return icarehome2;
    case 'csGen3BPDisplayKey':
    case 'csGen3BP_SM_DisplayKey':
      return andBpmGen3;
    // Gen 3 cuff sizes are catalogue rows in their own right and are priced as add-ons, so
    // they appear on Rates and pricing beside the monitors. Without them the lookup fell to the
    // default below, which returns the key itself and renders as a broken image.
    case 'csGen3BPLargeDisplayKey':
    case 'csGen3BPSmallDisplayKey':
    case 'csGen3BPWideRangeDisplayKey':
      return andBpmGen3Cuffs;
    case 'tactio4GBPDisplayKey':
    case 'bt106BPDisplayKey':
    case 'bt107cBPDisplayKey':
    case 'bodytrace4gBPDisplayKey':
      return BPMonitor;
    case 'tactio4GBodyScaleDisplayKey':
    case 'bt006BodyScaleDisplayKey':
    case 'bt005BodyScaleDisplayKey':
    case 'bodytrace4gscaleDisplayKey':
    case 'iScaleDisplayKey':
    case 'iScaleHighCapacityDisplayKey':
    case 'bt007ABodyScaleDisplayKey':
    case 'bt007BHighCapacityScaleDisplayKey':
      return bodyScale;
    case 'tactio4GGlucometerDisplayKey':
    case 'tactio4GGlucometerPack12DisplayKey':
    case 'tactio4GGlucometerPack4DisplayKey':
    case 'iGlucoseEssentialDisplayKey':
    case 'iGlucosePlusDisplayKey':
      return glucometer;
    case 'tactioBTLEPulseOxDisplayKey':
      return noninOx;
    case 'andMedicalBPCDisplayKey':
      return andMedicalBP;
    case 'andMedicalBPCDisplayLargeCuffKey':
      return andMedicalLargeCuff;
    case 'andMedicalScaleCDisplayKey':
      return andMedicalScale;
    case 'GarminVívofit4DisplayKey':
      return garminVivoFit4;
    case 'GarminVívofit4DisplaySMKey':
    case 'GarminVívofit4DisplayLargeKey':
    case 'GarminVívosmart4DisplayKey':
    case 'GarminVívosmart4DisplaySMKey':
    case 'GarminVívosmart4DisplayLargeKey':
      return garminVivoSmart4;
    case 'MIRSpirobankSmartDisplayKey':
      return mirSpirobankSmart;
    case 'MIRSmartOneDisplayKey':
      return mirSmartOne;
    case 'noninMedicalM3230DisplayKey':
      return noninOx;
    case 'rocheGuideDisplayKey':
      return rocheGuide;
    case 'rocheInstantDisplayKey':
      return rocheInstant;
    case 'rocheInstantglucoseStripDisplayKey':
      return rocheInstantStrips;
    case 'SamsungGalaxyA7DisplayKey':
      return samsungA7Galaxy;
    case 'tabletCaseOneDisplayKey':
    case 'tabletCaseTwoDisplayKey':
      return galaxyA7BookCover;
    case 'omronM7IntelliKey':
      return omronM7Intelli;
    case 'samsungGalaxyA8DisplayKey':
      return samsungGalaxyTabA8;
    case 'tactio4gPulseOxDisplayKey':
    case 'iPulseOxDisplayKey':
      return ipulseox;
    case 'transtekBGMg1DisplayKey':
      return transtekBGM;
    case 'transtekBPMg2DisplayKey':
      return transtekBPM;
    case 'transtekScaleG2DisplayKey':
      return transtekScale;
    case 'csDirectBPDisplayKey':
      return csDirectBPM;
    case 'csDirectBPXLDisplayKey':
      return csDirectBPMXL;
    case 'csDirectScaleDisplayKey':
      return csDirectScale;
    case 'iBPDisplayKey':
      return iBP;
    case 'iGlucoseTouchDisplayKey':
      return iGlucoseTouch;
    default:
      return deviceName;
  }
};

/**
 * The device's picture, or nothing when we have no asset for it.
 *
 * `getDeviceImage` falls back to returning the key it was given, which as an `<img src>` is a
 * request for a file that does not exist and renders as a broken image. Callers that can simply
 * omit the picture should ask here instead.
 */
export const getDeviceImageOrNone = (deviceName: string): string | undefined => {
  const image = getDeviceImage(deviceName);
  return image === deviceName ? undefined : image;
};

/**
 * Gets the outlined icon for a device based on its measurement type and name.
 * @param {String} measurementType the measurement this device collects (e.g., 'pressure', 'weight', 'glucose')
 * @param {String} deviceName the device display name
 * @returns the appropriate outlined icon for the device
 */
export const getDeviceIcon = (
  measurementType: string | undefined | null,
  deviceName: string | undefined | null
) => {
  return getDeviceIconByName(deviceName) ?? get4GDeviceIcon(measurementType);
};

/**
 * Gets the outlined icons for 4g device measurement types
 * @param {String} measurementType the measurement this device collects
 * @returns the appropriate outlined icon for the measurement
 */
export const get4GDeviceIcon = (measurementType: string | undefined | null) => {
  switch (measurementType) {
    case 'pressure':
      return FourgPressureMonitorOutlineIcon;
    case 'weight':
      return FourgScaleOutlineIcon;
    case 'glucose':
      return FourgDropOutlineIcon;
    case 'blood_oxygen':
      return FourgDropO2OutlineIcon;
    default:
      return undefined;
  }
};

/**
 * Gets the outlined icons for device by Name
 * @param {String} deviceName device name
 * @returns the appropriate outlined icon for the device
 */
export const getDeviceIconByName = (deviceName: string | undefined | null) => {
  switch (deviceName) {
    case 'Dexcom':
      return DropOutlineDexcomMiddleIcon;
    default:
      return undefined;
  }
};

/**
 * Get Battery Status Value for a single device
 * @param {Number} value is the battery level
 * @param {Any} manufacturer is the name of the device manufacturer
 */
export const getDeviceStatusValue = (value: number, manufacturer: any) => {
  return manufacturer === BODYTRACE_MANUFACTURER_NAME ? `${value} mV` : `${value} %`;
};

/**
 * Determine if a device has low battery based on thresholds
 * @param {Number} batteryLevel is the battery level
 * @param {String} deviceName is the name of the device
 * @param {String} manufacturer is the device manufacturer
 * @returns {Boolean} true if battery is low, false otherwise
 */
export const isDeviceBatteryLow = (
  batteryLevel: number,
  deviceName: string,
  manufacturer: string
): boolean => {
  if (!batteryLevel) return false;

  if (smartMeterManufacturerNames.includes(manufacturer)) {
    return batteryLevel <= 10;
  }

  if (manufacturer === BODYTRACE_MANUFACTURER_NAME) {
    switch (deviceName) {
      case 'tactio4GBPDisplayKey':
      case 'bt106BPDisplayKey':
      case 'bt107cBPDisplayKey':
      case 'bodytrace4gBPDisplayKey':
        return batteryLevel < 4700;
      case 'tactio4GBodyScaleDisplayKey':
      case 'bt007BHighCapacityScaleDisplayKey':
      case 'bt007ABodyScaleDisplayKey':
      case 'bt006BodyScaleDisplayKey':
      case 'bt005BodyScaleDisplayKey':
      case 'bodytrace4gscaleDisplayKey':
      case 'iScaleDisplayKey':
      case 'iScaleHighCapacityDisplayKey':
        return batteryLevel < 3300;
    }
  }

  return false;
};

/**
 * A cuff's circumference, as both screens say it: `6.3"–9.4" (16–24 cm)`.
 *
 * Extracted from `CuffSizeSelector`, which had it as a local function. "Small" and "Large" mean
 * nothing without the range they cover (Michel, 2026-09-15), so the price list needs the same
 * sentence — and a second copy of the formatting is how the two drift into disagreeing about a
 * measurement.
 */
export const formatDeviceSizeRange = (details: IDeviceOrderingDetails): string =>
  `${details.in.min}"–${details.in.max}" (${details.cm.min}–${details.cm.max} cm)`;

export const hasSmartMeterDevice = (devices: IDeviceListItem[] | undefined): boolean =>
  (devices ?? []).some((device) =>
    smartMeterManufacturerNames.includes(device.deviceOrdering?.manufacturer ?? '')
  );

/** The catalogue's name for a SKU, device or accessory, falling back to the SKU itself. */
export const deviceNameForSku = (
  intl: IntlShape,
  catalog: IDeviceOrderingCatalogItem[],
  sku: string
): string => {
  const entry = resolveCatalogEntry(sku, catalog);
  return entry ? intl.formatMessage({ id: entry.displayName, defaultMessage: sku }) : sku;
};

/** Whether anything in the cart ships through Amazon, which is what shows the shipping speed. */
export const cartHasAmazonItem = (
  cart: Record<string, number>,
  catalog: IDeviceOrderingCatalogItem[]
): boolean =>
  resolveCartItems(cart, catalog).some((item) => isAmazonFulfilled(item.shippingProvider));
